import { useState } from 'react';

import { ChevronDown, ChevronRight, Trash2 } from 'lucide-react';

import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { SelectItem } from '@/components/ui/select';
import Text from '@/components/ui/text';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type {
  FormGridColumn,
  FormGridRowData,
} from '@/components/ui/form-grid';
import type { ValidationState } from '@/theme/size-tokens';

export type FormGridRowProps = {
  /** Row data */
  row: FormGridRowData;
  /** Column definitions */
  columns: FormGridColumn[];
  /** Current nesting depth (0 = root) */
  depth: number;
  /** Maximum allowed nesting depth */
  maxDepth: number;
  /** Whether in mobile layout mode */
  isMobile: boolean;
  /** Size variant for form controls */
  size: string;
  /** Whether sub-rows are allowed (manual add button) */
  allowSubRows: boolean;
  /** Auto sub-row callback (when provided, sub-rows are auto-managed, no manual button) */
  autoSubRow?: (row: FormGridRowData) => boolean;
  /** Whether the row is disabled */
  disabled: boolean;
  /** Label for add sub-row button */
  addSubRowLabel: string;
  /** Callback when a field value changes */
  onValueChange: (rowId: string, key: string, value: unknown) => void;
  /** Callback to add a sub-row */
  onAddSubRow: (parentId: string) => void;
  /** Callback to remove this row */
  onRemove: (id: string) => void;
  /** Whether this row can be removed */
  canRemove: boolean;
  /** Validation states for fields */
  validationStates?: Record<string, ValidationState>;
};

// Depth indent classes (using pl-* for left padding)
const depthIndentClasses: Record<number, string> = {
  0: '',
  1: 'pl-8',
  2: 'pl-16',
  3: 'pl-24',
  4: 'pl-32',
  5: 'pl-40',
};

/** Check whether a row has any user-entered content (non-empty, non-default values) */
function rowHasContent(row: FormGridRowData): boolean {
  return Object.values(row.values).some(
    (v) => v !== '' && v !== false && v !== null && v !== undefined
  );
}

function getCheckboxColumnWidth(label: string): string {
  const minCh = 8;
  return `${Math.max(minCh, label.length + 2)}ch`;
}

/**
 * Convert a Tailwind width utility class to a valid CSS grid track value.
 * e.g. 'w-24' → '6rem', 'flex-1' → 'minmax(0, 1fr)', 'w-1/2' → '50%'
 */
function tailwindWidthToCss(width: string): string {
  if (width === 'flex-1' || width === 'w-full') return 'minmax(0, 1fr)';
  if (width === 'w-auto') return 'auto';
  // w-{number} → n * 0.25rem
  const numMatch = width.match(/^w-(\d+)$/);
  if (numMatch) return `${parseInt(numMatch[1]) * 0.25}rem`;
  // w-{num}/{den} fraction
  const fracMatch = width.match(/^w-(\d+)\/(\d+)$/);
  if (fracMatch)
    return `${((parseInt(fracMatch[1]) / parseInt(fracMatch[2])) * 100).toFixed(2)}%`;
  // Already a valid CSS value (contains a unit or keyword)
  if (
    /\d+(px|rem|em|%|fr|ch|vw|vh)/.test(width) ||
    /^(auto|min-content|max-content|fit-content)$/.test(width)
  ) {
    return width;
  }
  return 'auto';
}

function getDesktopGridTemplate(columns: FormGridColumn[]): string {
  const columnTracks = columns.map((column) => {
    if (column.width) {
      return tailwindWidthToCss(column.width);
    }
    if (column.type === 'checkbox') {
      return getCheckboxColumnWidth(column.label);
    }
    return 'minmax(0, 1fr)';
  });

  return `${columnTracks.join(' ')} 4rem`;
}

/**
 * FormGridRow - Recursive row component for FormGrid
 * Handles rendering of field controls and nested sub-rows
 */
function FormGridRow({
  row,
  columns,
  depth,
  maxDepth,
  isMobile,
  size,
  allowSubRows,
  autoSubRow,
  disabled,
  addSubRowLabel,
  onValueChange,
  onAddSubRow,
  onRemove,
  canRemove,
  validationStates = {},
}: Readonly<FormGridRowProps>) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const desktopGridTemplate = getDesktopGridTemplate(columns);
  const hasSubRows = row.subRows && row.subRows.length > 0;

  // Render a single field based on column type
  const renderField = (column: FormGridColumn, fieldId: string) => {
    const value = row.values[column.key];
    const validationState = validationStates[column.key] || 'neutral';

    const handleChange = (newValue: unknown) => {
      onValueChange(row.id, column.key, newValue);
    };

    switch (column.type) {
      case 'input':
        return (
          <Input
            id={fieldId}
            aria-label={column.label}
            value={(value as string) ?? ''}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={column.placeholder}
            type={column.inputType ?? 'text'}
            disabled={disabled}
            required={column.required}
            validationState={validationState}
          />
        );

      case 'select':
        return (
          <Select
            value={(value as string) ?? ''}
            onValueChange={(v) => handleChange(v)}
            disabled={disabled}
            required={column.required}
          >
            <SelectTrigger id={fieldId} aria-label={column.label}>
              <SelectValue placeholder={column.placeholder} />
            </SelectTrigger>
            <SelectContent>
              {column.options?.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case 'checkbox':
        if (column.key === 'required' && row.values.type === 'boolean') {
          return null;
        }

        return (
          <div
            className={`flex h-full items-center ${!isMobile ? 'justify-center' : ''}`}
          >
            <Checkbox
              id={fieldId}
              aria-label={column.label}
              checked={Boolean(value)}
              onCheckedChange={(checked) => handleChange(checked as boolean)}
              disabled={disabled}
            />
          </div>
        );

      case 'textarea':
        return (
          <Textarea
            id={fieldId}
            aria-label={column.label}
            value={(value as string) ?? ''}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={column.placeholder}
            disabled={disabled}
            required={column.required}
            validationState={validationState}
          />
        );

      case 'custom':
        if (column.render) {
          return column.render(value, row, handleChange, 0);
        }
        return null;

      default:
        return null;
    }
  };

  return (
    <div>
      {/* Row content */}
      <div
        className={` ${isMobile ? 'border-border bg-muted/20 flex flex-col gap-3 rounded-lg border px-3 py-3' : 'grid items-center gap-3 py-1'} `}
        style={
          !isMobile ? { gridTemplateColumns: desktopGridTemplate } : undefined
        }
      >
        {/* Fields */}
        {columns.map((column) => {
          const fieldId = `${row.id}-${column.key}`;
          return (
            <div
              key={column.key}
              className={`${isMobile ? 'w-full' : 'min-w-0'} ${column.type === 'checkbox' && !isMobile ? 'flex justify-center' : ''}`}
            >
              {/* Mobile: show label above field */}
              {isMobile && (
                <Text
                  as="label"
                  htmlFor={fieldId}
                  size="sm"
                  weight="medium"
                  variant="muted"
                  className="mb-1 block"
                  required={column.required}
                >
                  {column.label}
                </Text>
              )}
              {renderField(column, fieldId)}
            </div>
          );
        })}

        {/* Action buttons */}
        <div
          className={`flex items-center gap-1 ${isMobile ? 'justify-end' : 'w-16 shrink-0 justify-end'}`}
        >
          {canRemove && !isMobile && (
            <Button
              variant="ghost-error"
              size="icon-xs"
              aria-label="Remove row"
              onClick={() => onRemove(row.id)}
              disabled={disabled}
            >
              <Trash2 />
            </Button>
          )}
          {canRemove && isMobile && (
            <Button
              variant="link-error"
              aria-label="Remove row"
              onClick={() => onRemove(row.id)}
              disabled={disabled}
              size="sm"
            >
              Delete Field
              <Trash2 data-icon="inline-end" />
            </Button>
          )}
        </div>
      </div>

      {/* Sub-rows section with collapse toggle */}
      {hasSubRows && (
        <div
          className={`mt-1 ${depthIndentClasses[Math.min(depth + 1, 5)] || ''}`}
        >
          {/* Collapse toggle at the start of sub-rows */}
          <button
            type="button"
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="text-muted-foreground hover:text-foreground mb-1 flex cursor-pointer items-center gap-1 text-sm transition-colors"
            aria-label={isCollapsed ? 'Expand sub-rows' : 'Collapse sub-rows'}
          >
            {isCollapsed ? (
              <ChevronRight size={14} />
            ) : (
              <ChevronDown size={14} />
            )}
            <span>{isCollapsed ? 'Show properties' : 'Hide properties'}</span>
          </button>

          {/* Sub-rows (recursive) - collapsible */}
          {!isCollapsed && (
            <div className="space-y-1">
              {row.subRows!.map((subRow) => (
                <FormGridRow
                  key={subRow.id}
                  row={subRow}
                  columns={columns}
                  depth={depth + 1}
                  maxDepth={maxDepth}
                  isMobile={isMobile}
                  size={size}
                  allowSubRows={allowSubRows}
                  autoSubRow={autoSubRow}
                  disabled={disabled}
                  addSubRowLabel={addSubRowLabel}
                  onValueChange={onValueChange}
                  onAddSubRow={onAddSubRow}
                  onRemove={onRemove}
                  canRemove={rowHasContent(subRow) && row.subRows!.length > 1}
                  validationStates={validationStates}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

FormGridRow.displayName = 'FormGridRow';

export default FormGridRow;
